import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Spin, Button } from "antd";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import KPIBubbleChart from "../../components/WelcomePage/KPIBubbleChart";
import KPILineChart from "../../components/WelcomePage/KPILineChart";
import KPIDoughnutChart from "../../components/WelcomePage/KPIDoughnutChart";
import KPIBarChart from "../../components/WelcomePage/KPIBarChart";
import KPIStackedBarChart from "../../components/WelcomePage/KPIStackedBarChart";
import WelcomeTopicSentimentBarChart from "../../components/WelcomePage/WelcomeTopicSentimentBarChart";
import WelcomeTopicParticipationBarChart from "../../components/WelcomePage/WelcomeTopicParticipationBarChart";
import WelcomeTopicIntentBarChart from "../../components/WelcomePage/WelcomeTopicIntentBarChart";
import VOCScoreChart from "../../components/WelcomePage/VOCScoreChart";
import WelcomeMarketIntelligencePlanGate from "../Welcome/WelcomeMarketIntelligencePlanGate";
import { useSelector } from "react-redux";
import { List, Table } from "antd";
import { StatusUpdateComponent } from "pages/Dashboard/TopProblems";
import dayjs from "dayjs";
import styles from "./style.module.scss";

const IndividualWidget = ({ authenticated, user, match }) => {
  const { t } = useTranslation();
  const { widgetId } = useParams();
  const location = useLocation();
  const theme = 'light'; // Always use light theme for this page
  const subscriptionStatus = useSelector((state) => state.userReducer);
  
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShared, setIsShared] = useState(false);
  const [sharedHash, setSharedHash] = useState(null);

  // Get since and until from URL parameters or use last week as fallback
  const getDateRange = () => {
    const urlParams = new URLSearchParams(location.search);
    const since = urlParams.get('since');
    const until = urlParams.get('until');
    
    if (since && until) {
      return { since, until };
    }
    
    // Fallback to last week
    const endDate = dayjs();
    const startDate = endDate.subtract(6, 'day');
    return {
      since: startDate.format('YYYY-MM-DD'),
      until: endDate.format('YYYY-MM-DD')
    };
  };

  // Get user_id from URL parameters if it exists
  const getUserId = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('user_id');
  };

  useEffect(() => {
    if (!widgetId) {
      setError("No widget ID provided");
      setLoading(false);
      return;
    }

    fetchWidgetData();
  }, [widgetId, location.search]); // Re-fetch when URL parameters change

  const fetchWidgetData = async () => {
    try {
      setLoading(true);
      const { since, until } = getDateRange();
      const user_id = getUserId();
      
      const response = await apiRequest(
        "welcome/widget/data",
        "POST",
        { 
          widget_id: parseInt(widgetId),
          since: since,
          until: until,
          user_id: user_id
        },
        true
      );

      if (response?.success) {
        setWidget(response.success);
      } else {
        setError(t("individual_widget.load_failed", "Failed to load widget data"));
      }
    } catch (error) {
      console.error("Error fetching widget:", error);
      setError("Error loading widget data");
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.length ? value.join(", ") : "";
    }
    return String(value);
  };

  const renderWidget = (widget) => {
    if (!widget || !widget.data || Object.keys(widget.data).length === 0) {
      return <div className={styles.noDataMessage}>{t("individual_widget.no_data", "No data available")}</div>;
    }

    if (widget.data.error) {
      return <div className={styles.noDataMessage}>{widget.data.error}</div>;
    }

    const { data } = widget;

    if (widget.representation_type === 4) {
      const narrative = data.narrative || {};
      return (
        <div className={styles.smartNarrativeWidget}>
          {narrative.summary ? (
            <p className={styles.smartNarrativeSummary}>{narrative.summary}</p>
          ) : null}
          {Array.isArray(narrative.bullets) && narrative.bullets.length > 0 ? (
            <ul className={styles.smartNarrativeList}>
              {narrative.bullets.map((bullet, idx) => (
                <li key={`${widget.id}-smart-narrative-${idx}`}>{bullet}</li>
              ))}
            </ul>
          ) : (
            <div className={styles.noDataMessage}>{t("individual_widget.no_narrative", "No narrative available")}</div>
          )}
        </div>
      );
    }

    // Handle competitive metrics
    if (widget?.metricType === "competitive") {
      // Category-based visualization
      if (widget?.data?.comparison_type === 1) {
        if (!widget?.data?.data?.topics || widget?.data?.data?.topics.length === 0) {
          return <div className={styles.noDataMessage}>{t("individual_widget.no_category", "No category data available")}</div>;
        }

        if (widget?.representation_type === 1) {
          return (
            <div className={styles.categoryChartsContainer}>
              <div className={styles.doughnutCharts}>
                {widget?.data?.data?.topics?.map((topic) => {
                  if (!topic?.data?.datasets?.[0]?.data?.length) return null;
                  return (
                    <div key={topic?.id} className={styles.categoryChart}>
                      <KPIDoughnutChart
                        data={{
                          labels: topic?.data?.labels,
                          datasets: [{
                            data: topic?.data?.datasets?.[0]?.data,
                            backgroundColor: topic?.data?.datasets?.[0]?.backgroundColor,
                            borderWidth: 0,
                          }],
                        }}
                        title={topic?.name}
                        theme={theme}
                        showLegend={false}
                      />
                      <h4>{topic?.name}</h4>
                    </div>
                  );
                }).filter(Boolean)}
              </div>
              <div className={styles.Legend}>
                {widget?.data?.data?.topics?.[0]?.data?.labels?.map((label, index) => (
                  <div key={index}>
                    <span
                      className={styles.legendColor}
                      style={{
                        backgroundColor: widget?.data?.data?.topics?.[0]?.data?.datasets?.[0]?.backgroundColor?.[index],
                      }}
                    />
                    <span className={styles.legendLabel}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
      }
      // Brand-based visualization
      else if (widget?.data?.comparison_type === 2) {
        if (widget?.representation_type === 1) {
          return (
            <div className={styles.brandChartContainer}>
              <KPIStackedBarChart
                data={{
                  chart_type: "bar",
                  indexAxis: "y",
                  labels: widget?.data?.data?.labels,
                  datasets: widget?.data?.data?.datasets?.map((ds) => ({
                    ...ds,
                    barThickness: 16,
                    borderSkipped: false,
                    borderRadius: {
                      topLeft: 50,
                      topRight: 50,
                      bottomLeft: 50,
                      bottomRight: 50,
                    },
                  })),
                }}
                theme={theme}
              />
            </div>
          );
        }
      }
      // Time-based visualization
      else if (widget?.data?.comparison_type === 3) {
        if (widget?.representation_type === 1) {
          return (
            <div className={styles.timeChartsContainer}>
              {widget?.data?.data?.topics?.map((topic) => {
                const hasData = topic?.data?.datasets?.some(
                  (dataset) => dataset?.data && Array.isArray(dataset.data) && dataset.data.length > 0
                );
                if (!hasData) return null;

                return (
                  <div key={topic?.id} className={styles.timeChart}>
                    <h4>{topic?.name}</h4>
                    <KPILineChart
                      theme={theme}
                      data={{
                        labels: topic?.data?.labels,
                        datasets: topic?.data?.datasets?.map((ds) => ({
                          ...ds,
                          tension: 0.4,
                          pointRadius: 0,
                          pointHoverRadius: 4,
                          borderWidth: 2,
                          fill: false,
                          pointBorderWidth: 0,
                          pointHitRadius: 10,
                        })),
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        layout: {
                          padding: { left: 5, right: 5, top: 5, bottom: 5 },
                        },
                        scales: {
                          x: {
                            grid: { display: false },
                            ticks: { display: false },
                          },
                          y: {
                            display: false,
                            beginAtZero: true,
                            grid: { display: false },
                          },
                        },
                        plugins: {
                          legend: {
                            position: "bottom",
                            align: "start",
                            labels: {
                              boxWidth: 16,
                              usePointStyle: false,
                              padding: 15,
                              font: { size: 11 },
                              color: theme === 'dark' ? 'white' : 'black',
                            },
                          },
                          tooltip: {
                            enabled: true,
                            mode: "index",
                            intersect: false,
                            titleColor: theme === 'dark' ? "white" : "black",
                            bodyColor: theme === 'dark' ? "white" : "black",
                            cornerRadius: 4,
                            padding: 8,
                            titleFont: { size: 12 },
                            bodyFont: { size: 11 },
                            displayColors: true,
                            boxWidth: 6,
                            boxHeight: 6,
                            usePointStyle: true,
                            callbacks: {
                              title: (tooltipItems) => tooltipItems[0].label,
                              label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
                            },
                          },
                          datalabels: {
                            color: theme === 'dark' ? "white" : "black",
                            font: { size: 11 },
                            formatter: (value) => value,
                            display: true,
                            align: "top",
                            anchor: "end",
                          },
                        },
                      }}
                    />
                  </div>
                );
              }).filter(Boolean)}
            </div>
          );
        }
      } else if (widget?.data?.comparison_type === 4) {
        if (widget?.representation_type === 1) {
          return (
            <div className={styles.vocChartDynamic}>
              <VOCScoreChart
                data={widget?.data?.data}
                theme={theme}
                widget_id={widget?.id}
              />
            </div>
          );
        }
      }
    }

    // Handle non-competitive metrics
    if (widget.representation_type === 1) {
      // VOC score special widget
      if (data.chart_type === "voc_chart") {
        return (
          <WelcomeMarketIntelligencePlanGate
            metricId={widget?.metric_id}
            subscriptionRow={subscriptionStatus}
          >
            <div className={styles.vocChartContainer}>
              <VOCScoreChart
                data={widget?.data?.data}
                theme={theme}
                isSingleBrand={true}
                widget_id={widget?.id}
              />
            </div>
          </WelcomeMarketIntelligencePlanGate>
        );
      }

      // Chart widgets
      if (data.chart_type === "bar" || data.chart_type === "line") {
        if (!data.datasets?.[0]?.data?.length || !data.labels?.length) {
          return <div className={styles.noDataMessage}>{t("individual_widget.no_chart", "No chart data available")}</div>;
        }
      } else if (data.chart_type === "doughnut") {
        if (!data.datasets?.[0]?.data?.length) {
          return <div className={styles.noDataMessage}>{t("individual_widget.no_chart", "No chart data available")}</div>;
        }
      } else if (data.chart_type === "bubble") {
        if (!data.datasets?.[0]?.data?.length) {
          return <div className={styles.noDataMessage}>{t("individual_widget.no_chart", "No chart data available")}</div>;
        }
      } else if (data.chart_type === "stacked_bar") {
        if (!data.datasets?.length || !data.labels?.length) {
          return <div className={styles.noDataMessage}>{t("individual_widget.no_chart", "No chart data available")}</div>;
        }
      }

      // Render chart header if available
      const renderChartHeader = () => {
        if (!data.header) return null;

        const { title, subtitle, value, trend, icon } = data.header;

        // Special case for Review Statistics widget
        if (widget?.metric_id === 8) {
          return (
            <div className={styles.headerContent}>
              {value && (
                <div className={styles.headerValue}>
                  <span className={styles.headerValueText}>
                    Number of Reviews : {value}
                  </span>
                </div>
              )}
            </div>
          );
        }

        // Default header for other widgets
        return (
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <h4 title={title}>{title}</h4>
              {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            </div>
            {value && (
              <div className={styles.headerValue}>
                <span>{value}</span>
              </div>
            )}
          </div>
        );
      };

      return (
        <div className={`${styles.chartContainer} ${widget?.metric_id === 8 ? styles.reviewStatsContainer : ''}`}>
          {renderChartHeader()}
          
          {widget?.metric_id === 8 ? (
            <div className={styles.reviewStatsChartRow}>
              <div className={styles.chartContent}>
                {(() => {
                  switch (data.chart_type) {
                    case "bubble":
                      return <KPIBubbleChart data={data} theme={theme} />;
                    case "doughnut": {
                      const customLabelsD = data.legend ? data.legend.map(item => item.count) : null;
                      return (
                        <KPIDoughnutChart
                          data={data}
                          theme={theme}
                          customLabels={customLabelsD}
                          showLegend={widget?.metric_id === 12}
                        />
                      );
                    }
                    case "stacked_bar":
                      return widget?.metric_id === 14 ? (
                        <WelcomeTopicSentimentBarChart
                          payload={data}
                          widgetId={widget?.id}
                        />
                      ) : widget?.metric_id === 12 ? (
                        <WelcomeTopicIntentBarChart payload={data} />
                      ) : (
                        <KPIStackedBarChart data={data} theme={theme} />
                      );
                    case "line":
                      return <KPILineChart data={data} theme={theme} />;
                    case "bar":
                    default:
                      return widget?.metric_id === 15 ? (
                        <WelcomeTopicParticipationBarChart payload={data} />
                      ) : (
                        <KPIBarChart data={data} theme={theme} />
                      );
                  }
                })()}
              </div>
            </div>
          ) : (
            <div className={styles.chartContent}>
              {(() => {
                switch (data.chart_type) {
                  case "bubble":
                    return <KPIBubbleChart data={data} theme={theme} />;
                  case "doughnut": {
                    const legendLabels =
                      data.legend && Array.isArray(data.legend)
                        ? data.legend.map((item) => item.count)
                        : null;
                    return (
                      <KPIDoughnutChart
                        data={data}
                        theme={theme}
                        customLabels={legendLabels}
                        showLegend={widget?.metric_id === 12}
                      />
                    );
                  }
                  case "stacked_bar":
                    return widget?.metric_id === 14 ? (
                      <WelcomeTopicSentimentBarChart
                        payload={data}
                        widgetId={widget?.id}
                      />
                    ) : widget?.metric_id === 12 ? (
                      <WelcomeTopicIntentBarChart payload={data} />
                    ) : (
                      <KPIStackedBarChart data={data} theme={theme} />
                    );
                  case "line":
                    return <KPILineChart data={data} theme={theme} />;
                  case "bar":
                  default:
                    return widget?.metric_id === 15 ? (
                      <WelcomeTopicParticipationBarChart payload={data} />
                    ) : (
                      <KPIBarChart data={data} theme={theme} />
                    );
                }
              })()}
            </div>
          )}
        </div>
      );
    }

    // Handle list widgets
    if (widget.representation_type === 2) {
      if (!data.items || data.items.length === 0) {
        return <div className={styles.noDataMessage}>{t("individual_widget.no_list", "No list data available")}</div>;
      }

      return (
        <div className={styles.listWidget}>
          <List
            dataSource={data.items}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.itemLink}
                      >
                        {item.title}
                      </a>
                    ) : (
                      item.title
                    )
                  }
                  description={
                    item.descriptionLink ? (
                      <a
                        href={item.descriptionLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.itemLink}
                      >
                        {item.description}
                      </a>
                    ) : (
                      item.description
                    )
                  }
                />
              </List.Item>
            )}
          />
        </div>
      );
    }

    // Handle table widgets
    if (widget.representation_type === 3) {
      if (!data.items || data.items.length === 0) {
        return <div className={styles.noDataMessage}>No table data available</div>;
      }

      const aiInsightsColumn = [
        {
          title: "Area",
          dataIndex: "Area or Problem",
          key: "Area or Problem",
        },
        {
          title: "Root Cause",
          dataIndex: "Root Cause",
          key: "Root Cause",
        },
        {
          title: "Recommendation",
          dataIndex: "Recommendation",
          key: "Recommendation",
        },
        {
          title: "Status",
          key: "Status",
          dataIndex: "Status",
          render: (status, record) => (
            <StatusUpdateComponent
              record={record}
              onStatusUpdate={fetchWidgetData}
            />
          ),
        },
        {
          title: "Updated By",
          key: "Update By",
          render: (_, record) => {
            const formatted = record["Update Time"] ? dayjs(record["Update Time"])?.format("YYYY-MM-DD") : "";
            return (
              <div style={{ display: 'flex', flexDirection: "column", alignItems: "center", fontSize: 12 }}>
                <span>{record["Update By"]}</span>
                <span>{formatted}</span>
              </div>
            );
          }
        },
      ];

      const columns = widget?.metric_id === 19 ? aiInsightsColumn :
        data.columns ||
        Object.keys(data.items[0])
          .filter((key) => !key.endsWith("_link"))
          .map((key) => ({
            title: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
            dataIndex: key,
            key: key,
            render: (text, record) => {
              const linkField = `${key}_link`;
              if (record[linkField]) {
                return (
                  <a
                    href={record[linkField]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.itemLink}
                  >
                    {text}
                  </a>
                );
              }
              return text;
            },
          }));

      return (
        <div className={styles.tableWidget}>
          <Table
            dataSource={data.items.slice(0, 10).map((item, index) => ({
              ...item,
              key: index,
            }))}
            columns={columns}
            pagination={false}
            scroll={{}}
            size="default"
            className={styles.tableContainer}
            locale={{
              emptyText: (
                <div className={styles.noDataMessage}>No data available</div>
              ),
            }}
          />
        </div>
      );
    }

    return <div className={styles.noDataMessage}>Unsupported widget type</div>;
  };

  if (loading) {
    return (
      <div className={styles.blankPage}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <p>Loading widget...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.blankPage}>
        <div className={styles.errorContainer}>
          <h2>Error</h2>
          <p>{error}</p>
          <Button onClick={fetchWidgetData} type="primary">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!widget) {
    return (
      <div className={styles.blankPage}>
        <div className={styles.errorContainer}>
          <h2>Widget Not Found</h2>
          <p>The requested widget could not be found.</p>
          <Button onClick={fetchWidgetData} type="primary">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.blankPage}>
      <div className={styles.widgetContainer}>
        <div className={styles.widgetWrapper} data-testid="widget-container">
          <div className={styles.widgetTitleHeader}>
            <h4>{widget.name}</h4>
          </div>
          {renderWidget(widget)}
        </div>
      </div>
    </div>
  );
};

export default IndividualWidget;
